import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Shared open/close state for the mobile sidebar drawer.
 *
 * The hamburger lives in Dashboardheader (rendered at App level) while the
 * Sidebar and all of its props live inside Dashboard. This context is the only
 * link between them: Dashboard mounts the drawer and registers it, the header
 * just asks for it to be opened.
 */

// Default drawer width: Tailwind's `sm` breakpoint is overridden to 767px in
// tailwind.config.js.
export const MOBILE_DRAWER_BREAKPOINT = 767;
// The dashboard tab strips (Teacher/Admin sidebar and Student tabs) don't fit
// tablet widths, so both dashboards use the drawer up to here; the Teacher
// vertical sidebar returns at 1200px (see r-style.css).
export const TABLET_DRAWER_BREAKPOINT = 1200;

const noop = () => {};

const defaultValue = {
  isDrawerOpen: false,
  isDrawerAvailable: false,
  isDrawerActive: false,
  drawerBreakpoint: 0,
  openDrawer: noop,
  closeDrawer: noop,
  registerDrawer: noop,
};

const SidebarDrawerContext = createContext(defaultValue);

export const SidebarDrawerProvider = ({ children }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // 0 on routes where no sidebar exists (course pages etc.); otherwise the
  // width below which the mounted drawer replaces the inline nav.
  const [drawerBreakpoint, setDrawerBreakpoint] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Pass the drawer's breakpoint on mount and 0 (or false) on unmount.
  const registerDrawer = useCallback((breakpoint) => {
    const value =
      breakpoint === true ? MOBILE_DRAWER_BREAKPOINT : Number(breakpoint) || 0;
    setDrawerBreakpoint(value);
    // Unmounting the drawer must also drop any open state, otherwise the
    // backdrop would linger after navigating away from the dashboard.
    if (!value) setIsDrawerOpen(false);
  }, []);

  const isDrawerAvailable = drawerBreakpoint > 0;
  // True when the header should show the hamburger instead of the inline nav.
  const isDrawerActive = isDrawerAvailable && viewportWidth < drawerBreakpoint;

  // Resizing past the breakpoint brings the inline nav back — drop the drawer
  // so the user isn't left with a stray backdrop.
  useEffect(() => {
    if (!isDrawerActive) setIsDrawerOpen(false);
  }, [isDrawerActive]);

  const value = useMemo(
    () => ({
      isDrawerOpen,
      isDrawerAvailable,
      isDrawerActive,
      drawerBreakpoint,
      openDrawer,
      closeDrawer,
      registerDrawer,
    }),
    [
      isDrawerOpen,
      isDrawerAvailable,
      isDrawerActive,
      drawerBreakpoint,
      openDrawer,
      closeDrawer,
      registerDrawer,
    ],
  );

  return (
    <SidebarDrawerContext.Provider value={value}>
      {children}
    </SidebarDrawerContext.Provider>
  );
};

export const useSidebarDrawer = () => useContext(SidebarDrawerContext);

export default SidebarDrawerContext;
